import React, { useState, useEffect } from 'react';
import { FaX, FaCircleInfo } from 'react-icons/fa6';
import { getNextBusinessDay, formatDateForInput, isBusinessDay, getUpcomingHolidays } from '../../../utils/dateUtils';

const ConfirmationModal = ({
    isOpen,
    onClose,
    queue,
    handleRemove,
    handleSubmit,
    submitting,
}) => {
    // State for scheduled check date
    const [scheduledDate, setScheduledDate] = useState('');
    const [scheduledDateRaw, setScheduledDateRaw] = useState(null);
    const [showHolidays, setShowHolidays] = useState(false);
    const [upcomingHolidays, setUpcomingHolidays] = useState([]);
    const [overrideDate, setOverrideDate] = useState(false);

    // Initialize scheduled date when modal opens
    useEffect(() => {
        if (isOpen) {
            const nextBizDay = getNextBusinessDay();
            const formattedDate = formatDateForInput(nextBizDay);
            
            setScheduledDate(formattedDate);
            setScheduledDateRaw(nextBizDay); // Store the raw Date object
            setOverrideDate(false); // Reset override state when modal opens
            
            // Get upcoming holidays for reference
            const holidays = getUpcomingHolidays(6); // Next 6 months
            setUpcomingHolidays(holidays);
        }
    }, [isOpen]);

    // Check if selected date is a business day
    const isSelectedDateBusinessDay = scheduledDate ? isBusinessDay(new Date(scheduledDate)) : true;

    if (!isOpen) return null;

    const generateCSV = () => {
        // Define CSV headers
        const headers = [
            'ID',
            'Invoice Number',
            'Fund Name',
            'NTAF ID',
            'Payee',
            'Payment Method',
            'Amount Requested',
            'Notes',
            'Payee Address',
            'Address Label',
            'Address Does Not Match MIP'
        ];

        // Map the queue data to CSV rows
        const csvRows = queue.map(item => {
            // Create an array of values in the same order as headers
            return [
                item.id,
                item.ap_invoice_number,
                item.fund_name || '',
                item.ntaf_id || '',
                item.payee,
                item.payment_type || '',
                item.request_amount || '',
                item.description || '',
                item.payment_type === 'CHECK' ? (item.vendor_address || '') : '',
                item.payment_type === 'CHECK' ? (item.address_label || '') : '',
                item.payment_type === 'CHECK'
                    ? (item.address_does_not_match ? 'Yes' : '')
                    : ''
            ];
        });

        // Add headers as the first row
        csvRows.unshift(headers);

        // Convert to CSV format
        const csvContent = csvRows.map(row => row.map(value =>
            // Wrap values in quotes and escape existing quotes
            `"${String(value).replace(/"/g, '""')}"`
        ).join(',')).join('\n');

        // Generate dynamic filename: {payment_type}_{date}_mip_queue.csv
        const paymentType = queue.length > 0 && queue[0].payment_type
            ? queue[0].payment_type.toLowerCase()
            : 'unknown';
        const currentDate = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
        const filename = `${paymentType}_${currentDate}_mip_queue.csv`;

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSubmitAndDownload = async () => {
        // First submit the queue with the scheduled date
        await handleSubmit(scheduledDate);

        // Then generate and download the CSV
        generateCSV();
    };

    return (
        <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-50'>
            <div className='flex h-auto max-h-[90vh] w-[720px] max-w-lg flex-col rounded-lg bg-white p-6 shadow-lg'>
                <div className='flex-grow overflow-auto'>
                    <h2 className='text-left text-2xl font-semibold'>
                        Submit Check Queue to MIP
                    </h2>
                    <p className='text-gray-600 mt-2 text-left'>
                        Please ensure that all FRs for the day's batch are in
                        the queue before hitting submit. This will create a
                        check batch API session in MIP with all items with an
                        invoice date of the day submitted.
                    </p>

                    {/* Scheduled Check Date Section */}
                    <div className='mt-4 mb-4'>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Scheduled Check Date:
                        </label>
                        
                        {/* Display default date when not overriding */}
                        {!overrideDate && scheduledDateRaw && (
                            <div className='flex items-center gap-2 mb-2'>
                                <span className='text-sm text-gray-900 font-medium'>
                                    {scheduledDateRaw.toLocaleDateString('en-US', { 
                                        weekday: 'long', 
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric' 
                                    })}
                                </span>
                                <button
                                    type='button'
                                    onClick={() => setShowHolidays(!showHolidays)}
                                    className='text-blue-500 hover:text-blue-700 text-sm flex items-center gap-1'
                                    title='Show upcoming holidays'
                                >
                                    <FaCircleInfo size={14} />
                                    Holidays
                                </button>
                            </div>
                        )}

                        {/* Date input when overriding */}
                        {overrideDate && (
                            <div className='flex items-center gap-2'>
                                <input
                                    type='date'
                                    value={scheduledDate}
                                    onChange={(e) => setScheduledDate(e.target.value)}
                                    className={`border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        isSelectedDateBusinessDay 
                                            ? 'border-gray-300 focus:border-blue-500' 
                                            : 'border-orange-300 bg-orange-50 focus:border-orange-500'
                                    }`}
                                    disabled={submitting}
                                />
                                <button
                                    type='button'
                                    onClick={() => setShowHolidays(!showHolidays)}
                                    className='text-blue-500 hover:text-blue-700 text-sm flex items-center gap-1'
                                    title='Show upcoming holidays'
                                >
                                    <FaCircleInfo size={14} />
                                    Holidays
                                </button>
                            </div>
                        )}
                        
                        {overrideDate && !isSelectedDateBusinessDay && (
                            <div className='mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-sm text-orange-700'>
                                <strong>Note:</strong> Selected date falls on a weekend or US federal holiday. 
                                The system will process this as requested.
                            </div>
                        )}

                        <div className='flex items-center mt-2'>
                            <input
                                type='checkbox'
                                id='override-date'
                                checked={overrideDate}
                                onChange={(e) => setOverrideDate(e.target.checked)}
                                className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                                disabled={submitting}
                            />
                            <label htmlFor='override-date' className='ml-2 text-sm text-gray-600'>
                                Override this date
                            </label>
                        </div>

                        {/* Holidays Section */}
                        {showHolidays && upcomingHolidays.length > 0 && (
                            <div className='mt-3 p-3 bg-blue-50 border border-blue-200 rounded'>
                                <h4 className='text-sm font-medium text-blue-900 mb-2'>
                                    Upcoming US Federal Holidays
                                </h4>
                                <div className='text-xs text-blue-700 space-y-1 max-h-24 overflow-y-auto'>
                                    {upcomingHolidays.slice(0, 5).map((holiday, index) => (
                                        <div key={index} className='flex justify-between'>
                                            <span>{holiday.name}</span>
                                            <span className='font-mono'>{holiday.date}</span>
                                        </div>
                                    ))}
                                    {upcomingHolidays.length > 5 && (
                                        <div className='text-blue-500 italic'>
                                            ...and {upcomingHolidays.length - 5} more
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='mt-4 max-h-[50vh] overflow-auto'>
                        <table className='datatable-table datatable-one w-full table-auto border-collapse overflow-hidden px-4 md:table-fixed md:px-8'>
                            <thead className='border-separate px-4'>
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                >
                                    <th
                                        style={{
                                            paddingTop: '6px',
                                            paddingBottom: '6px',
                                            width: '60px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        ID
                                    </th>
                                    <th
                                        style={{
                                            paddingTop: '6px',
                                            paddingBottom: '6px',
                                        }}
                                    >
                                        Invoice Number
                                    </th>
                                    <th
                                        style={{
                                            paddingTop: '6px',
                                            paddingBottom: '6px',
                                        }}
                                    >
                                        Payee
                                    </th>
                                    <th
                                        style={{
                                            width: '60px',
                                            paddingTop: '6px',
                                            paddingBottom: '6px',
                                        }}
                                    ></th>
                                </tr>
                            </thead>
                            <tbody>
                                {queue?.map((item, index) => (
                                    <tr key={index}>
                                        <td
                                            style={{
                                                paddingTop: '6px',
                                                paddingBottom: '6px',
                                                width: '60px',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {item.id}
                                        </td>
                                        <td
                                            style={{
                                                paddingTop: '6px',
                                                paddingBottom: '6px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '150px',
                                            }}
                                        >
                                            {item.ap_invoice_number}
                                        </td>
                                        <td
                                            style={{
                                                paddingTop: '6px',
                                                paddingBottom: '6px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '200px',
                                            }}
                                        >
                                            {item.payee}
                                        </td>
                                        <td
                                            style={{
                                                paddingTop: '6px',
                                                paddingBottom: '6px',
                                                width: '60px',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <button
                                                onClick={() =>
                                                    handleRemove(item.id)
                                                }
                                                disabled={submitting}
                                                className="px-3 py-1"
                                            >
                                                <FaX
                                                    className='text-danger'
                                                    size={14}
                                                />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className='mt-4 flex flex-col'>
                    <div className='mb-4'>
                        <button
                            className='rounded-full border border-black px-6 py-2 text-black'
                            onClick={generateCSV}
                            disabled={submitting || !queue?.length}
                        >
                            DOWNLOAD CSV
                        </button>
                    </div>
                    <div className='flex justify-start gap-3'>
                        <button
                            onClick={onClose}
                            className='rounded-full border border-black px-6 py-2 text-black'
                        >
                            CANCEL
                        </button>
                        <button
                            className='rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                            onClick={handleSubmitAndDownload}
                            disabled={submitting}
                        >
                            {submitting ? 'SUBMITTING…' : 'SUBMIT & DOWNLOAD'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationModal;
